import { useApolloClient, useMutation } from '@apollo/client/react';
import gql from 'graphql-tag';
import { PROJECT_TOGGLE_OBJECT_VISIBILITY } from '../../../graphql/project';
import { ProjectToggleObjectVisibilityMutation, ProjectToggleObjectVisibilityMutationVariables } from '../../../types/graphqlTypes';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getAnnotationVisibleProperty } from '@pointorama/potree-shared';

/** Minimal fragment for reading visibility state from cache */
const PROJECT_VISIBILITY_FRAGMENT = gql`
  fragment ProjectVisibility on Project {
    id
    mapVisible
    state {
      annotations {
        identifier
      }
      wmsLayers {
        identifier
        visible
      }
      orthophotoLayers {
        identifier
        visible
      }
    }
    pointClouds {
      id
      visible
    }
  }
`;

/** Cache read type matching the visibility fragment */
type VisibilityProjectData = NonNullable<ProjectToggleObjectVisibilityMutation['projectToggleObjectVisibility']>;

export const useToggleHide = () => {
  const [toggleHide] = useMutation<ProjectToggleObjectVisibilityMutation, ProjectToggleObjectVisibilityMutationVariables>(PROJECT_TOGGLE_OBJECT_VISIBILITY);
  const apolloClient = useApolloClient();
  const { projectId = '' } = useParams();

  const onToggleHide = useCallback(
    ({ identifiers, visible }: { identifiers: string[]; visible: boolean }) => {
      const project = apolloClient.readFragment<VisibilityProjectData>({
        fragment: PROJECT_VISIBILITY_FRAGMENT,
        id: `Project:${projectId}`,
        fragmentName: 'ProjectVisibility',
      });
      if (!project) return;

      // Split annotation identifiers from non-annotation identifiers
      const annotationIdentifierSet = new Set(project.state.annotations.map((a) => a.identifier));
      const annotationIds = identifiers.filter((id) => annotationIdentifierSet.has(id));
      const nonAnnotationIds = identifiers.filter((id) => !annotationIdentifierSet.has(id));

      // Annotations: write to ReactiveProperty (Potree + persistence)
      if (annotationIds.length > 0) {
        try {
          for (const id of annotationIds) {
            getAnnotationVisibleProperty(id).value = visible;
          }
        } catch {
          // Property service not initialized yet
        }
      }

      // Non-annotations (point clouds, WMS, orthophoto, map): use old mutation with optimistic response
      if (nonAnnotationIds.length > 0) {
        const identifierSet = new Set(nonAnnotationIds);
        toggleHide({
          variables: { identifiers: nonAnnotationIds, projectId, visible },
          optimisticResponse: {
            __typename: 'Mutation',
            projectToggleObjectVisibility: {
              ...project,
              state: {
                ...project.state,
                annotations: project.state.annotations,
                wmsLayers: project.state.wmsLayers.map((wmsLayer) => (identifierSet.has(wmsLayer.identifier) ? { ...wmsLayer, visible } : wmsLayer)),
                orthophotoLayers: project.state.orthophotoLayers.map((layer) => (identifierSet.has(layer.identifier) ? { ...layer, visible } : layer)),
              },
              mapVisible: identifierSet.has('map') ? visible : project.mapVisible,
              pointClouds: project.pointClouds.map((pointCloud) => (identifierSet.has(pointCloud.id) ? { ...pointCloud, visible } : pointCloud)),
            },
          },
        });
      }
    },
    [apolloClient, projectId, toggleHide],
  );

  return { toggleHide: onToggleHide };
};
